import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Beaker, Calendar, Clock, Eye, Tag } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { blogService, BlogPost } from '../services/blogService';

const AboutDeveloper: React.FC = () => {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTag, setSelectedTag] = useState('');
  const [allTags, setAllTags] = useState<string[]>([]);

  useEffect(() => {
    loadPosts();
  }, []);

  const loadPosts = async () => {
    try {
      const publishedPosts = await blogService.getPublishedPosts();
      setPosts(publishedPosts);
      
      // 获取所有标签
      const tags = [...new Set(publishedPosts.flatMap(post => post.tags))];
      setAllTags(tags);
    } catch (error) {
      console.error('加载博客文章失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/');
  };

  const handlePostClick = (postId: string) => {
    navigate(`/blog/${postId}`);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // 过滤文章
  const filteredPosts = posts.filter(post => {
    const matchesTag = selectedTag === '' || post.tags.includes(selectedTag);
    return matchesTag;
  });

  return (
    <div className="min-h-screen bg-[#3A7D78] text-white flex flex-col">
      {/* 导航栏 */}
      <nav className="fixed top-0 left-0 right-0 bg-[#3A7D78]/95 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <button 
              onClick={handleBack}
              className="flex items-center gap-2 text-white hover:text-[#F5B6C1] transition-colors"
            >
              <Beaker className="h-8 w-8 text-[#F5B6C1]" />
              <span className="text-xl font-bold text-white">人格向量实验室</span>
            </button>
          </div>
        </div>
      </nav>

      {/* 主内容区域 */}
      <section className="pt-32 pb-8 px-4 flex-1">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="text-center mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
                关于这个站点的
                <span className="text-[#F5B6C1]"> 故事 </span>
              </h1>
            </div>

            {/* 筛选功能（简化版） */}
            {allTags.length > 0 && (
              <div className="mb-8 flex justify-center">
                <select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  className="px-4 py-2 bg-[#2A6D68] rounded-lg text-white text-sm shadow-inner border-t border-white/10 border-l border-white/10 border-b border-r border-b-[#1A5D58]/80 border-r-[#1A5D58]/80 focus:outline-none focus:ring-2 focus:ring-[#F5B6C1] focus:border-transparent"
                >
                  <option value="">所有标签</option>
                  {allTags.map(tag => (
                    <option key={tag} value={tag}>{tag}</option>
                  ))}
                </select>
              </div>
            )}

            {/* 博客文章列表 */}
            {loading ? (
              <div className="text-center py-12">
                <div className="text-white/60 text-lg">加载中...</div>
              </div>
            ) : filteredPosts.length === 0 ? (
              <div className="text-center py-12">
                <div className="bg-white rounded-md p-12 shadow-lg border border-gray-200">
                  <div className="text-[#2A6D68] text-lg">
                    {posts.length === 0 ? (
                      <>
                        <p className="mb-4">📝 还没有发布任何文章</p>
                        <p className="text-sm">敬请期待开发者的精彩分享</p>
                      </>
                    ) : (
                      <>
                        <p className="mb-4">🔍 没有找到匹配的文章</p>
                        <p className="text-sm">试试调整搜索条件或标签筛选</p>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <div className="space-y-8">
                {filteredPosts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    onClick={() => handlePostClick(post.id)}
                    className="bg-white rounded-md p-8 shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-300 cursor-pointer group flex flex-col md:flex-row gap-6"
                  >
                    {/* 封面图片 */}
                    {post.coverImage && (
                      <div className="md:w-1/3 overflow-hidden rounded-md">
                        <img
                          src={post.coverImage}
                          alt={post.title}
                          className="w-full h-48 md:h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      </div>
                    )}
                    
                    {/* 文章内容 */}
                    <div className={`flex-1 ${post.coverImage ? 'md:w-2/3' : 'w-full'}`}>
                      {/* 文章标题 */}
                      <h2 className="text-2xl md:text-3xl font-bold text-[#1A5D58] mb-4 group-hover:text-[#2A6D68] transition-colors">
                        {post.title}
                      </h2>
                      
                      {/* 文章摘要 */}
                      <p className="text-[#2A6D68] mb-6 text-lg leading-relaxed">
                        {post.excerpt}
                      </p>
                      
                      {/* 标签 */}
                      {post.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2 mb-4">
                          {post.tags.slice(0, 4).map((tag, tagIndex) => (
                            <span
                              key={tagIndex}
                              className="px-3 py-1 bg-[#1A5D58] text-[#F5B6C1] rounded-full text-sm border-t border-l border-white/5 shadow-inner"
                            >
                              {tag}
                            </span>
                          ))}
                          {post.tags.length > 4 && (
                            <span className="px-3 py-1 bg-[#1A5D58] text-white/60 rounded-full text-sm border-t border-l border-white/5 shadow-inner">
                              +{post.tags.length - 4}
                            </span>
                          )}
                        </div>
                      )}
                      
                      {/* 文章元信息 */}
                      <div className="flex items-center justify-between text-[#2A6D68]/70">
                        <div className="flex items-center gap-6">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            <span>{formatDate(post.publishedAt)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            <span>{post.readTime}分钟阅读</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Eye className="h-4 w-4" />
                          <span>{post.views} 次阅读</span>
                        </div>
                      </div>
                    </div>
                  </motion.article>
                ))}
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* 页脚 - 固定在底部 */}
      <footer className="py-4 text-center mt-auto">
        <div className="flex flex-wrap justify-center gap-6 text-lg font-semibold text-white">
          <a 
            href="https://www.zhihu.com/people/he-luo-wu" 
            target="_blank" 
            rel="noopener noreferrer"
            className="hover:text-[#F5B6C1] transition-colors duration-200"
          >
            🔍知乎：屁屁怪他爸爸主人
          </a>
          <span>🔍公众号：语言行为</span>
          <span>🔍播客：识人识己</span>
        </div>
      </footer>
    </div>
  );
};

export default AboutDeveloper;